import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

import { getUser } from "@/lib/api/user";
import type { User } from "@/types/user";

export const USER_QUERY_KEY = ["user"] as const;

interface UseUserQueryOptions<TData> {
  enabled?: boolean;
  select?: (user: User) => TData;
  refetchInterval?: UseQueryOptions<User, Error, TData>["refetchInterval"];
}

// Everything that needs the user (identity, settings, Getting started) must
// read through this single ["user"] key via `select`, so it is fetched once.
// The post-login flow warms this cache.
export function useUserQuery<TData = User>(
  options: UseUserQueryOptions<TData> = {}
) {
  return useQuery({
    queryKey: USER_QUERY_KEY,
    queryFn: () => getUser().then((res) => res.data),
    // Identity/settings change rarely and mutations invalidate this key, so
    // hold it fresh for a few minutes instead of refetching on every mount.
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}
